import React, { useEffect, useMemo } from "react";
import { bodyFront } from "../../assets/body-highlighter/bodyFront.ts";
import { bodyBack } from "../../assets/body-highlighter/bodyBack.ts";
import { BodyPart as RawBodyPart } from "../../assets/body-highlighter";

export type Slug = RawBodyPart["slug"];

export interface HighlightPart {
  slug: Slug;
  intensity?: number;
  side?: "left" | "right";
}

export interface BodyHighlighterProps {
  /** which view: front or back */
  side: "front" | "back";
  /** data specifying which slugs to highlight and with what intensity */
  data: HighlightPart[];
  /** scale factor for the SVG */
  scale?: number;
  /** callback when a part is clicked */
  onPartClick?: (part: HighlightPart, side?: "left" | "right") => void;
}

export const BodyHighlighter: React.FC<BodyHighlighterProps> = ({
  side,
  data,
  scale = 1,
  onPartClick,
}) => {
  // pick the correct raw source
  const source: RawBodyPart[] = side === "front" ? bodyFront : bodyBack;

  // merge and colorize
  const parts = useMemo(() => {
    return source.map((raw) => {
      const match = data.find((d) => d.slug === raw.slug);
      return {
        ...raw,
        intensity: match?.intensity || 0,
        side: match?.side,
        color: match
          ? // pick color based on intensity (e.g. darker for higher)
            // you can adapt this gradient or array
            ["#ddd", "#74b9ff", "#0984e3"][match.intensity || 1]
          : "#ddd",
      };
    });
  }, [data, side]);

  const viewBox = side === "back" ? "700 0 700 1400" : "0 0 700 1400";

  // render all paths
  return (
    <svg
      viewBox={viewBox}
      width={700 * scale}
      height={1400 * scale}
      style={{ display: "block" }}
    >
      {parts.map((part) => {
        // safely handle possibly undefined paths
        const paths = [
          ...(part.path?.common ?? []),
          ...(part.path?.left ?? []),
          ...(part.path?.right ?? []),
        ];
        return paths.map((d, i) => (
          <path
            key={`${part.slug}-${i}`}
            d={d}
            fill={part.color}
            stroke="#000"
            strokeWidth={0.5}
            onClick={() =>
              onPartClick?.(
                { slug: part.slug, intensity: part.intensity, side: part.side },
                part.side
              )
            }
            style={{ cursor: onPartClick ? "pointer" : "default" }}
          />
        ));
      })}
    </svg>
  );
};
